import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getAuth, onAuthStateChanged, signOut } from "firebase/auth";
import {
  addDoc,
  collection,
  doc,
  getDoc,
  getFirestore,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  updateDoc,
  writeBatch,
} from "firebase/firestore";
import app from "../../firebase.js";

const STATUS_OPTIONS = [
  {
    value: "soumise",
    label: "Demande soumise",
    progress: 40,
    color: "#2563eb",
    background: "#eff6ff",
  },
  {
    value: "en_cours_etude",
    label: "En cours d’étude",
    progress: 65,
    color: "#d97706",
    background: "#fffbeb",
  },
  {
    value: "informations_complementaires",
    label: "Informations complémentaires requises",
    progress: 75,
    color: "#7c3aed",
    background: "#f5f3ff",
  },
  {
    value: "acceptee",
    label: "Demande acceptée",
    progress: 100,
    color: "#059669",
    background: "#ecfdf5",
  },
  {
    value: "refusee",
    label: "Demande refusée",
    progress: 100,
    color: "#dc2626",
    background: "#fef2f2",
  },
];

const RETIRED_STATUS = {
  value: "retiree",
  label: "Demande retirée",
  progress: 0,
  color: "#64748b",
  background: "#f1f5f9",
};

const getStatusConfiguration = (status) => {
  if (status === RETIRED_STATUS.value) {
    return RETIRED_STATUS;
  }

  return STATUS_OPTIONS.find((item) => item.value === status) || {
    value: status,
    label: "Finalisation en attente",
    progress: 20,
    color: "#d97706",
    background: "#fffbeb",
  };
};

const formatDate = (timestamp) => {
  if (!timestamp?.toDate) {
    return "Date indisponible";
  }

  return timestamp.toDate().toLocaleString("fr-FR", {
    day: "2-digit",
    month: "long",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

function FinanceAdmin() {
  const navigate = useNavigate();

  const [currentUser, setCurrentUser] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [authLoading, setAuthLoading] = useState(true);
  const [requests, setRequests] = useState([]);
  const [requestsLoading, setRequestsLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("tous");
  const [updatingId, setUpdatingId] = useState("");
  const [expandedId, setExpandedId] = useState("");
  const [adminMessages, setAdminMessages] = useState({});
  const [message, setMessage] = useState(null);

  useEffect(() => {
    const auth = getAuth(app);
    const database = getFirestore(app);

    const unsubscribe = onAuthStateChanged(
      auth,
      async (user) => {
        setCurrentUser(user);

        if (!user) {
          setIsAdmin(false);
          setAuthLoading(false);
          navigate("/consulting/finance/connexion", {
            replace: true,
          });
          return;
        }

        try {
          const adminDocument = await getDoc(
            doc(database, "admins", user.uid)
          );

          if (!adminDocument.exists()) {
            setIsAdmin(false);
            setAuthLoading(false);
            navigate("/consulting/finance", {
              replace: true,
            });
            return;
          }

          setIsAdmin(true);
          setAuthLoading(false);
        } catch (error) {
          console.error(
            "Erreur lors de la vérification administrateur :",
            error
          );

          setIsAdmin(false);
          setAuthLoading(false);
          navigate("/consulting/finance", {
            replace: true,
          });
        }
      },
      (error) => {
        console.error("Erreur d’authentification :", error);
        setAuthLoading(false);
      }
    );

    return () => unsubscribe();
  }, [navigate]);

  useEffect(() => {
    if (!isAdmin) {
      return undefined;
    }

    const database = getFirestore(app);

    const requestsQuery = query(
      collection(database, "financeRequests"),
      orderBy("createdAt", "desc")
    );

    const unsubscribe = onSnapshot(
      requestsQuery,
      (snapshot) => {
        const loadedRequests = snapshot.docs.map((requestDocument) => ({
          id: requestDocument.id,
          ...requestDocument.data(),
        }));

        setRequests(loadedRequests);
        setRequestsLoading(false);
      },
      (error) => {
        console.error(
          "Erreur lors du chargement des demandes :",
          error
        );

        setMessage({
          type: "error",
          text:
            "Impossible de charger les dossiers. Les autorisations administrateur Firestore doivent être configurées.",
        });

        setRequestsLoading(false);
      }
    );

    return () => unsubscribe();
  }, [isAdmin]);

  const filteredRequests = useMemo(() => {
    const normalizedSearch = search.trim().toLowerCase();

    return requests.filter((request) => {
      const matchesStatus =
        statusFilter === "tous" || request.status === statusFilter;

      const searchableContent = [
        request.reference,
        request.userEmail,
        request.profileLabel,
        request.financingLabel,
        request.formData?.firstName,
        request.formData?.lastName,
        request.formData?.prenom,
        request.formData?.nom,
        request.formData?.telephone,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      const matchesSearch =
        !normalizedSearch ||
        searchableContent.includes(normalizedSearch);

      return matchesStatus && matchesSearch;
    });
  }, [requests, search, statusFilter]);

  const handleStatusChange = async (requestId, newStatus) => {
    if (!isAdmin || !requestId || !newStatus) {
      return;
    }

    const selectedStatus = getStatusConfiguration(newStatus);
    const currentRequest = requests.find(
      (request) => request.id === requestId
    );

    if (currentRequest?.status === "retiree") {
      setMessage({
        type: "error",
        text:
          "Cette demande a été retirée par le demandeur et ne peut plus être traitée.",
      });
      return;
    }

    const adminMessage = String(
      adminMessages[requestId] ?? currentRequest?.adminMessage ?? ""
    ).trim();

    if (
      newStatus === "informations_complementaires" &&
      !adminMessage
    ) {
      setMessage({
        type: "error",
        text:
          "Ajoutez un message indiquant précisément les informations complémentaires demandées.",
      });
      return;
    }

    const confirmation = window.confirm(
      `Confirmer le passage du dossier au statut « ${selectedStatus.label} »${
        adminMessage ? " avec le message destiné au client" : ""
      } ?`
    );

    if (!confirmation) {
      return;
    }

    setUpdatingId(requestId);
    setMessage(null);

    try {
      const database = getFirestore(app);

      await updateDoc(
        doc(database, "financeRequests", requestId),
        {
          status: newStatus,
          submissionComplete: true,
          updatedAt: serverTimestamp(),
          statusUpdatedAt: serverTimestamp(),
          statusUpdatedBy: currentUser.uid,
          statusUpdatedByEmail: currentUser.email || "",
          adminMessage,
        }
      );

      setMessage({
        type: "success",
        text: `Le dossier a été placé au statut « ${selectedStatus.label} ».`,
      });
    } catch (error) {
      console.error(
        "Erreur lors de la modification du statut :",
        error
      );

      setMessage({
        type: "error",
        text:
          "Le statut n’a pas pu être modifié. Vérifiez les règles Firestore administrateur.",
      });
    } finally {
      setUpdatingId("");
    }
  };

  const handleLogout = async () => {
    try {
      await signOut(getAuth(app));
      navigate("/consulting/finance", {
        replace: true,
      });
    } catch (error) {
      console.error("Erreur lors de la déconnexion :", error);
    }
  };

  if (authLoading) {
    return (
      <main style={styles.centeredPage}>
        <div style={styles.loader} />
        <p style={styles.loadingText}>
          Vérification des autorisations…
        </p>
      </main>
    );
  }

  if (!isAdmin) {
    return null;
  }

  return (
    <main style={styles.page}>
      <header style={styles.header}>
        <button
          type="button"
          style={styles.backButton}
          onClick={() => navigate("/consulting/finance")}
        >
          ← Retour à HDC Finance
        </button>

        <div style={styles.brand}>
          <div style={styles.brandIcon}>H</div>

          <div>
            <div style={styles.brandTitle}>HDC Finance</div>
            <div style={styles.brandSubtitle}>
              Administration sécurisée
            </div>
          </div>
        </div>

        <button
          type="button"
          style={styles.logoutButton}
          onClick={handleLogout}
        >
          Se déconnecter
        </button>
      </header>

      <section style={styles.hero}>
        <div style={styles.adminBadge}>
          ⚙️ Administration Finance
        </div>

        <h1 style={styles.title}>
          Gestion des demandes
        </h1>

        <p style={styles.subtitle}>
          Consultez les dossiers reçus et mettez à jour leur
          état d’avancement.
        </p>

        <div style={styles.adminIdentity}>
          <span style={styles.identityLabel}>
            Administrateur connecté
          </span>

          <strong>{currentUser?.email}</strong>
        </div>
      </section>

      <section style={styles.content}>
        <div style={styles.statistics}>
          <article style={styles.statisticCard}>
            <span style={styles.statisticLabel}>
              Total des dossiers
            </span>
            <strong style={styles.statisticValue}>
              {requests.length}
            </strong>
          </article>

          <article style={styles.statisticCard}>
            <span style={styles.statisticLabel}>
              En cours d’étude
            </span>
            <strong style={styles.statisticValue}>
              {
                requests.filter(
                  (request) =>
                    request.status === "en_cours_etude"
                ).length
              }
            </strong>
          </article>

          <article style={styles.statisticCard}>
            <span style={styles.statisticLabel}>
              Dossiers acceptés
            </span>
            <strong style={styles.statisticValue}>
              {
                requests.filter(
                  (request) => request.status === "acceptee"
                ).length
              }
            </strong>
          </article>

          <article style={styles.statisticCard}>
            <span style={styles.statisticLabel}>
              Dossiers retirés
            </span>
            <strong style={styles.statisticValue}>
              {
                requests.filter(
                  (request) => request.status === "retiree"
                ).length
              }
            </strong>
          </article>
        </div>

        <div style={styles.filters}>
          <input
            type="search"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Rechercher par référence, nom ou e-mail…"
            style={styles.searchInput}
          />

          <select
            value={statusFilter}
            onChange={(event) =>
              setStatusFilter(event.target.value)
            }
            style={styles.filterSelect}
          >
            <option value="tous">Tous les statuts</option>
            <option value="en_attente_documents">
              Finalisation en attente
            </option>

            <option value="retiree">
              Demande retirée
            </option>

            {STATUS_OPTIONS.map((status) => (
              <option
                key={status.value}
                value={status.value}
              >
                {status.label}
              </option>
            ))}
          </select>
        </div>

        {message && (
          <div
            style={{
              ...styles.message,
              ...(message.type === "success"
                ? styles.successMessage
                : styles.errorMessage),
            }}
          >
            {message.text}
          </div>
        )}

        {requestsLoading ? (
          <div style={styles.stateCard}>
            Chargement des dossiers…
          </div>
        ) : filteredRequests.length === 0 ? (
          <div style={styles.stateCard}>
            Aucun dossier ne correspond à votre recherche.
          </div>
        ) : (
          <div style={styles.requestsList}>
            {filteredRequests.map((request) => {
              const status = getStatusConfiguration(
                request.status
              );

              const isExpanded = expandedId === request.id;
              const isWithdrawn = request.status === "retiree";

              return (
                <article
                  key={request.id}
                  style={styles.requestCard}
                >
                  <div style={styles.requestHeader}>
                    <div>
                      <span style={styles.smallLabel}>
                        Numéro du dossier
                      </span>

                      <h2 style={styles.reference}>
                        {request.reference || request.id}
                      </h2>

                      <p style={styles.userEmail}>
                        {request.userEmail ||
                          "Adresse e-mail indisponible"}
                      </p>
                    </div>

                    <span
                      style={{
                        ...styles.statusBadge,
                        color: status.color,
                        background: status.background,
                      }}
                    >
                      {status.label}
                    </span>
                  </div>

                  <div style={styles.informationGrid}>
                    <div style={styles.informationBlock}>
                      <span style={styles.smallLabel}>
                        Profil
                      </span>
                      <strong>
                        {request.profileLabel || "Non renseigné"}
                      </strong>
                    </div>

                    <div style={styles.informationBlock}>
                      <span style={styles.smallLabel}>
                        Financement
                      </span>
                      <strong>
                        {request.financingLabel ||
                          "Non renseigné"}
                      </strong>
                    </div>

                    <div style={styles.informationBlock}>
                      <span style={styles.smallLabel}>
                        Justificatifs
                      </span>
                      <strong>
                        {request.documentCount || 0}
                      </strong>
                    </div>

                    <div style={styles.informationBlock}>
                      <span style={styles.smallLabel}>
                        Date de création
                      </span>
                      <strong>
                        {formatDate(request.createdAt)}
                      </strong>
                    </div>
                  </div>

                  <div style={styles.progressSection}>
                    <div style={styles.progressHeader}>
                      <span>{status.label}</span>
                      <strong style={{ color: status.color }}>
                        {status.progress} %
                      </strong>
                    </div>

                    <div style={styles.progressTrack}>
                      <div
                        style={{
                          ...styles.progressBar,
                          width: `${status.progress}%`,
                          background: status.color,
                        }}
                      />
                    </div>
                  </div>

                  {isWithdrawn && (
                    <div style={styles.withdrawnNotice}>
                      <span style={styles.withdrawnNoticeIcon}>↩️</span>

                      <div>
                        <strong style={styles.withdrawnNoticeTitle}>
                          Dossier retiré par le demandeur
                        </strong>

                        <p style={styles.withdrawnNoticeText}>
                          Cette demande a été retirée le{" "}
                          {formatDate(request.withdrawnAt)}. Elle est
                          conservée dans l’historique, mais elle ne peut
                          plus être traitée.
                        </p>
                      </div>
                    </div>
                  )}

                  <div style={styles.adminMessageSection}>
                    <label
                      htmlFor={`admin-message-${request.id}`}
                      style={styles.adminMessageLabel}
                    >
                      Message destiné au client
                    </label>

                    <textarea
                      id={`admin-message-${request.id}`}
                      value={
                        adminMessages[request.id] ??
                        request.adminMessage ??
                        ""
                      }
                      disabled={
                        updatingId === request.id || isWithdrawn
                      }
                      onChange={(event) =>
                        setAdminMessages((previousMessages) => ({
                          ...previousMessages,
                          [request.id]: event.target.value,
                        }))
                      }
                      placeholder="Exemple : Votre dossier est en cours d’étude. Nous vous contacterons si une information supplémentaire est nécessaire."
                      maxLength={1000}
                      style={{
                        ...styles.adminMessageInput,
                        ...(isWithdrawn
                          ? styles.disabledAdminMessageInput
                          : {}),
                      }}
                    />

                    <span style={styles.adminMessageHelp}>
                      Ce message sera visible par le client après la mise à jour du statut.
                    </span>
                  </div>

                  <AdminConversation
                    request={request}
                    currentUser={currentUser}
                  />

                  <div style={styles.actions}>
                    {isWithdrawn ? (
                      <div style={styles.lockedStatus}>
                        🔒 Traitement désactivé — demande retirée
                      </div>
                    ) : (
                      <select
                        value={request.status || ""}
                        disabled={updatingId === request.id}
                        onChange={(event) =>
                          handleStatusChange(
                            request.id,
                            event.target.value
                          )
                        }
                        style={styles.statusSelect}
                      >
                        <option value="en_attente_documents">
                          Finalisation en attente
                        </option>

                        {STATUS_OPTIONS.map((option) => (
                          <option
                            key={option.value}
                            value={option.value}
                          >
                            {option.label}
                          </option>
                        ))}
                      </select>
                    )}

                    <button
                      type="button"
                      style={styles.detailsButton}
                      onClick={() =>
                        setExpandedId(
                          isExpanded ? "" : request.id
                        )
                      }
                    >
                      {isExpanded
                        ? "Masquer les informations"
                        : "Voir les informations"}
                    </button>
                  </div>

                  {updatingId === request.id && (
                    <p style={styles.updateText}>
                      Mise à jour du dossier…
                    </p>
                  )}

                  {isExpanded && (
                    <div style={styles.detailsPanel}>
                      <h3 style={styles.detailsTitle}>
                        Informations fournies
                      </h3>

                      <div style={styles.detailsGrid}>
                        {Object.entries(
                          request.formData || {}
                        ).map(([key, value]) => (
                          <div
                            key={key}
                            style={styles.detailItem}
                          >
                            <span style={styles.detailKey}>
                              {key}
                            </span>

                            <strong style={styles.detailValue}>
                              {typeof value === "object"
                                ? JSON.stringify(value)
                                : String(value || "Non renseigné")}
                            </strong>
                          </div>
                        ))}
                      </div>

                      <h3 style={styles.detailsTitle}>
                        Justificatifs transmis par e-mail
                      </h3>

                      <div style={styles.documentsList}>
                        {(request.documents || []).map(
                          (documentItem, index) => (
                            <div
                              key={`${documentItem.name}-${index}`}
                              style={styles.documentItem}
                            >
                              <span>📎</span>

                              <div>
                                <strong>
                                  {documentItem.name ||
                                    `Document ${index + 1}`}
                                </strong>

                                <div style={styles.documentType}>
                                  {documentItem.type ||
                                    "Type indisponible"}
                                </div>
                              </div>
                            </div>
                          )
                        )}

                        {!request.documents?.length && (
                          <p style={styles.emptyDocuments}>
                            Aucun justificatif enregistré.
                          </p>
                        )}
                      </div>
                    </div>
                  )}
                </article>
              );
            })}
          </div>
        )}
      </section>

      
    </main>
  );
}

function AdminConversation({ request, currentUser }) {
  const [messages, setMessages] = useState([]);
  const [reply, setReply] = useState("");
  const [isSending, setIsSending] = useState(false);
  const [conversationError, setConversationError] = useState("");

  const isWithdrawn = request.status === "retiree";
  const unreadUserMessages = messages.filter(
    (messageItem) =>
      messageItem.senderRole === "user" &&
      messageItem.readByAdmin !== true
  );
  const requesterName = [
    request.formData?.firstName || request.formData?.prenom,
    request.formData?.lastName || request.formData?.nom,
  ]
    .filter(Boolean)
    .join(" ")
    .trim()
    || request.formData?.representativeName
    || request.formData?.companyName
    || "Demandeur";

  useEffect(() => {
    const database = getFirestore(app);
    const messagesQuery = query(
      collection(
        database,
        "financeRequests",
        request.id,
        "messages"
      ),
      orderBy("createdAt", "asc")
    );

    const unsubscribe = onSnapshot(
      messagesQuery,
      (snapshot) => {
        setMessages(
          snapshot.docs.map((messageDocument) => ({
            id: messageDocument.id,
            ...messageDocument.data(),
          }))
        );
        setConversationError("");
      },
      (error) => {
        console.error(
          "Erreur lors du chargement de la conversation :",
          error
        );
        setConversationError(
          "Impossible de charger les échanges de ce dossier."
        );
      }
    );

    return () => unsubscribe();
  }, [request.id]);

  const handleSendReply = async () => {
    const trimmedReply = reply.trim();

    if (!trimmedReply || isSending || isWithdrawn) {
      return;
    }

    if (trimmedReply.length > 1000) {
      setConversationError(
        "Le message ne doit pas dépasser 1 000 caractères."
      );
      return;
    }

    setIsSending(true);
    setConversationError("");

    try {
      const database = getFirestore(app);

      await addDoc(
        collection(
          database,
          "financeRequests",
          request.id,
          "messages"
        ),
        {
          text: trimmedReply,
          senderId: currentUser.uid,
          senderEmail: currentUser.email || "",
          senderRole: "admin",
          readByUser: false,
          readByAdmin: true,
          createdAt: serverTimestamp(),
        }
      );

      setReply("");
    } catch (error) {
      console.error("Erreur lors de l’envoi du message :", error);
      setConversationError(
        "Le message n’a pas pu être envoyé. Vérifiez les règles Firestore."
      );
    } finally {
      setIsSending(false);
    }
  };

  const handleMarkAsRead = async () => {
    if (unreadUserMessages.length === 0) {
      return;
    }

    setConversationError("");

    try {
      const database = getFirestore(app);
      const batch = writeBatch(database);

      unreadUserMessages.forEach((messageItem) => {
        batch.update(
          doc(
            database,
            "financeRequests",
            request.id,
            "messages",
            messageItem.id
          ),
          {
            readByAdmin: true,
          }
        );
      });

      await batch.commit();
    } catch (error) {
      console.error(
        "Erreur lors de la confirmation de lecture :",
        error
      );
      setConversationError(
        "Impossible de marquer les messages comme lus."
      );
    }
  };

  return (
    <div style={styles.conversationCard}>
      <div style={styles.conversationHeader}>
        <div>
          <strong style={styles.conversationTitle}>
            Conversation avec {requesterName}
          </strong>
          <span style={styles.conversationSubtitle}>
            {request.userEmail || "Utilisateur du dossier"}
          </span>
        </div>

        <div style={styles.conversationIndicators}>
          {unreadUserMessages.length > 0 && (
            <button
              type="button"
              onClick={handleMarkAsRead}
              style={styles.unreadButton}
            >
              🔔 {unreadUserMessages.length} nouveau
              {unreadUserMessages.length > 1 ? "x" : ""} message
              {unreadUserMessages.length > 1 ? "s" : ""} — Marquer comme lu
            </button>
          )}

          <span style={styles.messageCount}>
            {messages.length} message{messages.length > 1 ? "s" : ""}
          </span>
        </div>
      </div>

      {messages.length === 0 ? (
        <div style={styles.emptyConversation}>
          Aucun message du demandeur pour le moment.
        </div>
      ) : (
        <div style={styles.conversationMessages}>
          {messages.map((messageItem) => {
            const isAdminMessage = messageItem.senderRole === "admin";

            return (
              <div
                key={messageItem.id}
                style={{
                  ...styles.conversationMessage,
                  ...(isAdminMessage
                    ? styles.adminConversationMessage
                    : styles.userConversationMessage),
                }}
              >
                <div style={styles.messageAuthorRow}>
                  <strong style={styles.messageAuthor}>
                    {isAdminMessage
                      ? "HDC Finance"
                      : requesterName}
                  </strong>

                  <span style={styles.messageDate}>
                    {formatDate(messageItem.createdAt)}
                  </span>
                </div>

                <p style={styles.conversationMessageText}>
                  {messageItem.text}
                </p>
              </div>
            );
          })}
        </div>
      )}

      {conversationError && (
        <div style={styles.conversationError} role="alert">
          ⚠️ {conversationError}
        </div>
      )}

      {isWithdrawn ? (
        <div style={styles.conversationLocked}>
          🔒 Conversation fermée : cette demande a été retirée.
        </div>
      ) : (
        <div style={styles.conversationReplySection}>
          <label
            htmlFor={`conversation-reply-${request.id}`}
            style={styles.conversationReplyLabel}
          >
            Répondre à {requesterName}
          </label>

          <textarea
            id={`conversation-reply-${request.id}`}
            value={reply}
            onChange={(event) => setReply(event.target.value)}
            placeholder={`Écrivez votre réponse à ${requesterName}…`}
            maxLength={1000}
            disabled={isSending}
            style={styles.conversationReplyInput}
          />

          <div style={styles.conversationReplyFooter}>
            <span style={styles.characterCount}>
              {reply.length}/1000 caractères
            </span>

            <button
              type="button"
              disabled={!reply.trim() || isSending}
              onClick={handleSendReply}
              style={{
                ...styles.sendConversationButton,
                ...(!reply.trim() || isSending
                  ? styles.disabledConversationButton
                  : {}),
              }}
            >
              {isSending
                ? "Envoi en cours…"
                : "Envoyer le message"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

const styles = {
  page: {
    minHeight: "100vh",
    background: "#f4f7fb",
    color: "#0f172a",
    fontFamily: "Arial, sans-serif",
  },

  centeredPage: {
    minHeight: "100vh",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: "16px",
    background: "#070b1e",
    color: "#ffffff",
    fontFamily: "Arial, sans-serif",
  },

  loader: {
    width: "42px",
    height: "42px",
    borderRadius: "50%",
    border: "4px solid rgba(255,255,255,0.2)",
    borderTopColor: "#7c3aed",
  },

  loadingText: {
    margin: 0,
    color: "#cbd5e1",
  },

  header: {
    minHeight: "72px",
    padding: "0 5%",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "20px",
    background: "#070b1e",
    borderBottom: "1px solid rgba(255,255,255,0.1)",
    color: "#ffffff",
    flexWrap: "wrap",
  },

  backButton: {
    border: "none",
    background: "transparent",
    color: "#ffffff",
    fontWeight: "800",
    cursor: "pointer",
  },

  brand: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
  },

  brandIcon: {
    width: "38px",
    height: "38px",
    borderRadius: "12px",
    display: "grid",
    placeItems: "center",
    background:
      "linear-gradient(135deg, #7c3aed, #2563eb)",
    color: "#ffffff",
    fontWeight: "900",
  },

  brandTitle: {
    fontWeight: "900",
  },

  brandSubtitle: {
    marginTop: "2px",
    color: "#94a3b8",
    fontSize: "11px",
  },

  logoutButton: {
    padding: "10px 14px",
    border: "1px solid rgba(255,255,255,0.2)",
    borderRadius: "10px",
    background: "rgba(255,255,255,0.06)",
    color: "#ffffff",
    fontWeight: "700",
    cursor: "pointer",
  },

  hero: {
    padding: "70px 5% 85px",
    textAlign: "center",
    background:
      "linear-gradient(135deg, #070b1e, #111936)",
    color: "#ffffff",
  },

  adminBadge: {
    display: "inline-block",
    padding: "9px 14px",
    borderRadius: "999px",
    background: "rgba(124,58,237,0.18)",
    border: "1px solid rgba(167,139,250,0.35)",
    color: "#ddd6fe",
    fontWeight: "800",
    fontSize: "13px",
  },

  title: {
    margin: "22px 0 12px",
    fontSize: "clamp(36px, 6vw, 64px)",
    lineHeight: 1,
    fontWeight: "900",
  },

  subtitle: {
    margin: "0 auto",
    maxWidth: "700px",
    color: "#cbd5e1",
    lineHeight: 1.7,
  },

  adminIdentity: {
    margin: "26px auto 0",
    width: "fit-content",
    padding: "12px 18px",
    display: "flex",
    flexDirection: "column",
    gap: "4px",
    borderRadius: "14px",
    background: "rgba(255,255,255,0.08)",
    border: "1px solid rgba(255,255,255,0.12)",
    fontSize: "13px",
  },

  identityLabel: {
    color: "#94a3b8",
    fontSize: "10px",
    textTransform: "uppercase",
    letterSpacing: "0.08em",
  },

  content: {
    width: "min(1180px, 90%)",
    margin: "-38px auto 0",
    paddingBottom: "80px",
  },

  statistics: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(210px, 1fr))",
    gap: "16px",
    marginBottom: "22px",
  },

  statisticCard: {
    padding: "22px",
    display: "flex",
    flexDirection: "column",
    gap: "8px",
    background: "#ffffff",
    borderRadius: "18px",
    boxShadow: "0 15px 45px rgba(15,23,42,0.08)",
  },

  statisticLabel: {
    color: "#64748b",
    fontSize: "13px",
    fontWeight: "700",
  },

  statisticValue: {
    fontSize: "30px",
  },

  filters: {
    marginBottom: "20px",
    padding: "18px",
    display: "grid",
    gridTemplateColumns:
      "minmax(240px, 1fr) minmax(220px, 320px)",
    gap: "14px",
    background: "#ffffff",
    borderRadius: "18px",
    boxShadow: "0 15px 45px rgba(15,23,42,0.06)",
  },

  searchInput: {
    width: "100%",
    padding: "14px",
    border: "1px solid #dbe3ef",
    borderRadius: "12px",
    outline: "none",
    fontSize: "14px",
    boxSizing: "border-box",
  },

  filterSelect: {
    width: "100%",
    padding: "14px",
    border: "1px solid #dbe3ef",
    borderRadius: "12px",
    background: "#ffffff",
    fontWeight: "700",
    boxSizing: "border-box",
  },

  message: {
    marginBottom: "18px",
    padding: "15px 18px",
    borderRadius: "13px",
    fontWeight: "700",
  },

  successMessage: {
    background: "#ecfdf5",
    border: "1px solid #86efac",
    color: "#047857",
  },

  errorMessage: {
    background: "#fef2f2",
    border: "1px solid #fca5a5",
    color: "#b91c1c",
  },

  stateCard: {
    padding: "50px 20px",
    textAlign: "center",
    background: "#ffffff",
    borderRadius: "20px",
    color: "#64748b",
    boxShadow: "0 15px 45px rgba(15,23,42,0.07)",
  },

  requestsList: {
    display: "grid",
    gap: "20px",
  },

  requestCard: {
    padding: "24px",
    background: "#ffffff",
    border: "1px solid #e2e8f0",
    borderRadius: "22px",
    boxShadow: "0 18px 55px rgba(15,23,42,0.08)",
  },

  requestHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: "18px",
    flexWrap: "wrap",
  },

  smallLabel: {
    display: "block",
    marginBottom: "6px",
    color: "#94a3b8",
    fontSize: "10px",
    fontWeight: "900",
    textTransform: "uppercase",
    letterSpacing: "0.06em",
  },

  reference: {
    margin: 0,
    fontSize: "21px",
  },

  userEmail: {
    margin: "7px 0 0",
    color: "#64748b",
    fontSize: "13px",
  },

  statusBadge: {
    padding: "9px 13px",
    borderRadius: "999px",
    fontSize: "12px",
    fontWeight: "900",
  },

  informationGrid: {
    marginTop: "22px",
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))",
    gap: "12px",
  },

  informationBlock: {
    padding: "15px",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    background: "#f8fafc",
    borderRadius: "13px",
    fontSize: "13px",
  },

  progressSection: {
    marginTop: "18px",
    padding: "16px",
    background: "#f8fafc",
    borderRadius: "14px",
  },

  progressHeader: {
    marginBottom: "12px",
    display: "flex",
    justifyContent: "space-between",
    gap: "12px",
    fontSize: "13px",
    fontWeight: "800",
  },

  progressTrack: {
    height: "7px",
    overflow: "hidden",
    borderRadius: "999px",
    background: "#e2e8f0",
  },

  progressBar: {
    height: "100%",
    borderRadius: "999px",
    transition: "width 0.3s ease",
  },

  actions: {
    marginTop: "20px",
    display: "flex",
    justifyContent: "space-between",
    gap: "12px",
    flexWrap: "wrap",
  },

  adminMessageSection: {
    marginTop: "18px",
    padding: "16px",
    background: "#f8fafc",
    border: "1px solid #e2e8f0",
    borderRadius: "14px",
  },

  withdrawnNotice: {
    marginTop: "18px",
    padding: "16px",
    display: "flex",
    alignItems: "flex-start",
    gap: "12px",
    border: "1px solid #cbd5e1",
    borderRadius: "14px",
    background: "#f8fafc",
  },

  withdrawnNoticeIcon: {
    fontSize: "21px",
  },

  withdrawnNoticeTitle: {
    color: "#475569",
    fontSize: "13px",
    fontWeight: "900",
  },

  withdrawnNoticeText: {
    margin: "6px 0 0",
    color: "#64748b",
    fontSize: "12px",
    lineHeight: 1.6,
  },

  adminMessageLabel: {
    display: "block",
    marginBottom: "9px",
    color: "#0f172a",
    fontSize: "13px",
    fontWeight: "900",
  },

  adminMessageInput: {
    width: "100%",
    minHeight: "90px",
    padding: "13px",
    border: "1px solid #cbd5e1",
    borderRadius: "12px",
    background: "#ffffff",
    color: "#0f172a",
    fontFamily: "inherit",
    fontSize: "13px",
    lineHeight: 1.6,
    resize: "vertical",
    outline: "none",
    boxSizing: "border-box",
  },

  disabledAdminMessageInput: {
    background: "#e2e8f0",
    color: "#64748b",
    cursor: "not-allowed",
  },

  adminMessageHelp: {
    display: "block",
    marginTop: "8px",
    color: "#64748b",
    fontSize: "11px",
  },

  conversationCard: {
    marginTop: "18px",
    padding: "17px",
    border: "1px solid #bfdbfe",
    borderRadius: "14px",
    background: "#ffffff",
  },

  conversationHeader: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: "14px",
    flexWrap: "wrap",
  },

  conversationTitle: {
    display: "block",
    color: "#1e3a8a",
    fontSize: "13px",
    fontWeight: "900",
  },

  conversationSubtitle: {
    display: "block",
    marginTop: "4px",
    color: "#64748b",
    fontSize: "10px",
    overflowWrap: "anywhere",
  },

  messageCount: {
    padding: "6px 10px",
    borderRadius: "999px",
    background: "#eff6ff",
    color: "#2563eb",
    fontSize: "9px",
    fontWeight: "900",
  },

  conversationIndicators: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: "8px",
    flexWrap: "wrap",
  },

  unreadButton: {
    padding: "6px 10px",
    border: "1px solid #fbbf24",
    borderRadius: "999px",
    background: "#fffbeb",
    color: "#b45309",
    fontFamily: "inherit",
    fontSize: "9px",
    fontWeight: "900",
    cursor: "pointer",
  },

  emptyConversation: {
    marginTop: "14px",
    padding: "13px",
    borderRadius: "11px",
    background: "#f8fafc",
    color: "#64748b",
    fontSize: "11px",
  },

  conversationMessages: {
    marginTop: "15px",
    display: "grid",
    gap: "10px",
  },

  conversationMessage: {
    maxWidth: "82%",
    padding: "12px 14px",
    borderRadius: "13px",
  },

  adminConversationMessage: {
    marginLeft: "auto",
    border: "1px solid #ddd6fe",
    background: "#ede9fe",
  },

  userConversationMessage: {
    marginRight: "auto",
    border: "1px solid #bfdbfe",
    background: "#eff6ff",
  },

  messageAuthorRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "12px",
    flexWrap: "wrap",
  },

  messageAuthor: {
    color: "#334155",
    fontSize: "10px",
    fontWeight: "900",
  },

  messageDate: {
    color: "#94a3b8",
    fontSize: "8px",
  },

  conversationMessageText: {
    margin: "7px 0 0",
    color: "#334155",
    fontSize: "11px",
    lineHeight: 1.6,
    whiteSpace: "pre-wrap",
    overflowWrap: "anywhere",
  },

  conversationError: {
    marginTop: "13px",
    padding: "10px 12px",
    border: "1px solid #fecaca",
    borderRadius: "10px",
    background: "#fef2f2",
    color: "#b91c1c",
    fontSize: "10px",
    fontWeight: "800",
  },

  conversationReplySection: {
    marginTop: "15px",
    paddingTop: "14px",
    borderTop: "1px solid #e2e8f0",
  },

  conversationReplyLabel: {
    display: "block",
    marginBottom: "8px",
    color: "#0f172a",
    fontSize: "11px",
    fontWeight: "900",
  },

  conversationReplyInput: {
    width: "100%",
    minHeight: "88px",
    padding: "12px",
    border: "1px solid #cbd5e1",
    borderRadius: "11px",
    background: "#ffffff",
    color: "#0f172a",
    fontFamily: "inherit",
    fontSize: "11px",
    lineHeight: 1.6,
    resize: "vertical",
    outline: "none",
    boxSizing: "border-box",
  },

  conversationReplyFooter: {
    marginTop: "9px",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "12px",
    flexWrap: "wrap",
  },

  characterCount: {
    color: "#94a3b8",
    fontSize: "9px",
  },

  sendConversationButton: {
    padding: "10px 14px",
    border: "none",
    borderRadius: "10px",
    background: "linear-gradient(135deg, #7c3aed, #2563eb)",
    color: "#ffffff",
    fontSize: "10px",
    fontWeight: "900",
    cursor: "pointer",
  },

  disabledConversationButton: {
    opacity: 0.55,
    cursor: "not-allowed",
  },

  conversationLocked: {
    marginTop: "15px",
    padding: "11px 13px",
    borderRadius: "10px",
    background: "#f1f5f9",
    color: "#64748b",
    fontSize: "10px",
    fontWeight: "800",
  },

  statusSelect: {
    minWidth: "260px",
    padding: "13px",
    border: "1px solid #cbd5e1",
    borderRadius: "12px",
    background: "#ffffff",
    fontWeight: "800",
  },

  lockedStatus: {
    minWidth: "260px",
    padding: "13px",
    border: "1px solid #cbd5e1",
    borderRadius: "12px",
    background: "#f1f5f9",
    color: "#64748b",
    fontSize: "12px",
    fontWeight: "800",
  },

  detailsButton: {
    padding: "13px 17px",
    border: "none",
    borderRadius: "12px",
    background:
      "linear-gradient(135deg, #7c3aed, #2563eb)",
    color: "#ffffff",
    fontWeight: "800",
    cursor: "pointer",
  },

  updateText: {
    margin: "14px 0 0",
    color: "#7c3aed",
    fontSize: "13px",
    fontWeight: "700",
  },

  detailsPanel: {
    marginTop: "20px",
    padding: "20px",
    borderRadius: "16px",
    background: "#f8fafc",
    border: "1px solid #e2e8f0",
  },

  detailsTitle: {
    margin: "0 0 15px",
    fontSize: "16px",
  },

  detailsGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(210px, 1fr))",
    gap: "10px",
    marginBottom: "22px",
  },

  detailItem: {
    padding: "12px",
    background: "#ffffff",
    borderRadius: "10px",
  },

  detailKey: {
    display: "block",
    marginBottom: "5px",
    color: "#64748b",
    fontSize: "11px",
  },

  detailValue: {
    fontSize: "13px",
    wordBreak: "break-word",
  },

  documentsList: {
    display: "grid",
    gap: "9px",
  },

  documentItem: {
    padding: "12px",
    display: "flex",
    alignItems: "center",
    gap: "10px",
    background: "#ffffff",
    borderRadius: "10px",
  },

  documentType: {
    marginTop: "3px",
    color: "#64748b",
    fontSize: "11px",
  },

  emptyDocuments: {
    margin: 0,
    color: "#64748b",
  },

  footer: {
    minHeight: "70px",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    gap: "10px",
    background: "#070b1e",
    color: "#94a3b8",
    fontSize: "13px",
  },
};

export default FinanceAdmin;